import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Input, message, Row, Tooltip } from 'antd';
import PasswordHint from '../PasswordHint/PasswordHint';
import usePasswordValidate from '../../lib/utils/usePasswordValidate';
import axios, { AxiosResponse } from 'axios';
import AuthService from '../../services/AuthService';
import { changeUserPasswordRoute } from '../../lib/routes/users';
import { ReactComponent as Exit } from '../../lib/img/exit.svg';
import './ForcePasswordChange.less';
import LocalStorageService from '../../services/LocalStorageService';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { changePersonInfoDescriptions } from '../../store/slices/personsSlice/personsSlice';
import { Link } from 'react-router-dom';

const ForcePasswordChange = () => {
  const currentUser = AuthService.getCurrentUser();
  const dispatch = useAppDispatch();

  const { personInfoDescriptions } = useAppSelector((state) => state.persons);

  const [passwordForm] = Form.useForm();

  const [isPassButtonDisabled, setIsPassButtonDisabled] = useState(true);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isOldPasswordValid, setIsOldPasswordValid] = useState(true);

  const onReset = () => {
    passwordForm.resetFields();
    setIsOldPasswordValid(true);
    setIsPasswordValid(false);
    setIsPassButtonDisabled(true);
  };

  useEffect(() => {
    return () => {
      onReset();
    };
  }, []);

  const {
    isRepeatedPasswordValidator,
    isPasswordValidator,
    handleKeyPress,
    handlePaste,
    isPasswordSameValidator,
    isPasswordReusedValidator,
  } = usePasswordValidate();

  // Для нового пароля проверки (чтобы показывать одно сообщение об ошибке одновременно)
  const combinedPasswordValidator = async () => {
    try {
      await isPasswordSameValidator({ form: passwordForm });
    } catch {
      throw new Error(' ');
    }

    try {
      await isPasswordValidator({ form: passwordForm });
    } catch {
      throw new Error('Пароль слишком простой');
    }

    try {
      await isPasswordReusedValidator({ form: passwordForm });
    } catch {
      throw new Error('Новый пароль совпадает с текущим');
    }

    return Promise.resolve();
  };

  // Для проверки всех полей (для кнопки сохранить)
  const allCombinedPasswordValidator = async () => {
    try {
      await isPasswordValidator({ form: passwordForm });
    } catch {
      throw new Error('Пароль слишком простой');
    }

    try {
      await isPasswordReusedValidator({ form: passwordForm });
    } catch {
      throw new Error('Новый пароль совпадает с текущим');
    }

    try {
      await isRepeatedPasswordValidator({ form: passwordForm });
    } catch {
      throw new Error('Пароль не совпадает');
    }

    return Promise.resolve();
  };

  const onFinish = (values) => {
    const handleChangePassword = (args: { oldPassword: string; newPassword: string }) => {
      const { newPassword, oldPassword } = args;
      if (currentUser?.user_id) {
        return axios
          .patch(changeUserPasswordRoute(currentUser.user_id), {
            newPassword,
            oldPassword,
          })
          .then((res) => {
            message.destroy();
            message.success('Пароль успешно изменён');
            return res;
          })
          .catch((err) => {
            if (err.response.data.code !== 1000) {
              message.destroy();
              message.error('Непредвиденная ошибка');
              return err;
            }
            return err;
          });
      }
    };

    handleChangePassword({ oldPassword: values.oldPassword, newPassword: values.newPassword })?.then((res) => {
      const responseAxios = res as AxiosResponse & { response: any };
      if (responseAxios.status === 204 && currentUser?.user_id) {
        dispatch(
          changePersonInfoDescriptions({
            userId: currentUser.user_id,
            newPersonInfoDescriptions: { dsc: '' },
          })
        );
        onReset();
      }
      if (responseAxios && responseAxios.response)
        if (responseAxios.response?.data?.code === 1000) {
          setIsOldPasswordValid(false);
        }
    });
  };

  const passwordChangeInfo = () => {
    return (
      <Col span={24}>
        <PasswordHint mtListItem={8} />
        <Form
          form={passwordForm}
          name="basic"
          initialValues={{
            remember: true,
          }}
          layout="vertical"
          onFinish={onFinish}
          style={{ marginTop: '14px' }}
          onValuesChange={(field, fields) => {
            combinedPasswordValidator()
              .then(() => setIsPasswordValid(true))
              .catch(() => setIsPasswordValid(false));

            if (Object.values(fields).some((item) => item === undefined || item === '')) {
              setIsPassButtonDisabled(true);
            } else {
              allCombinedPasswordValidator()
                .then(() => setIsPassButtonDisabled(false))
                .catch(() => setIsPassButtonDisabled(true));
            }
          }}
        >
          <Form.Item
            label="Текущий пароль"
            name="oldPassword"
            dependencies={['newPassword', 'repeatedPassword', 'oldPassword']}
            className="mt10"
            rules={[
              {
                required: true,
                message: '',
              },
            ]}
            validateStatus={!isOldPasswordValid ? 'error' : undefined}
            help={!isOldPasswordValid ? 'Введённый пароль не совпадает с текущим' : undefined}
          >
            <Input.Password
              className="force-password-change-input"
              onChange={() => setIsOldPasswordValid(true)}
           /*   onKeyPress={handleKeyPress}*/
              onPaste={handlePaste}
              maxLength={32}
            />
          </Form.Item>
          <Form.Item
            dependencies={['newPassword', 'repeatedPassword', 'oldPassword']}
            label="Новый пароль"
            name="newPassword"
            validateStatus={isPasswordValid ? 'success' : undefined}
            help={isPasswordValid ? 'Надёжный пароль' : undefined}
            rules={[
              {
                required: true,
                message: '',
              },
              {
                validator: combinedPasswordValidator,
              },
            ]}
            className={`mt10 ${isPasswordValid ? 'validate-success-message' : ''}`}
          >
            <Input.Password
              className="force-password-change-input"
              onKeyPress={handleKeyPress}
              onPaste={handlePaste}
              maxLength={32}
            />
          </Form.Item>
          <Form.Item
            label="Подтвердите пароль"
            name="repeatedPassword"
            dependencies={['newPassword', 'repeatedPassword', 'oldPassword']}
            rules={[
              {
                required: true,
                message: '',
              },
              {
                validator: () => isRepeatedPasswordValidator({ form: passwordForm }),
                message: 'Пароль не совпадает',
              },
            ]}
            className="mt10"
          >
            <Input.Password
              className="force-password-change-input"
              onKeyPress={handleKeyPress}
              onPaste={handlePaste}
              maxLength={32}
            />
          </Form.Item>
        </Form>
      </Col>
    );
  };

  const handleLogout = () => {
    LocalStorageService.clearToken();
    window.location.reload();
  };

  return (
    <div className="force-password-change">
      <div className="force-password-change-container">
        <Row className="logout">
          <Tooltip title="Выйти">
            <Exit className="cursor-pointer" onClick={handleLogout} />
          </Tooltip>
        </Row>

        <div className="force-password-change-innerContainer">
          <Row className="force-password-change-innerContainer-header">
            <div className="logo" />

            <div className="force-password-change-innerContainer-header-title">Уважаемый пользователь!</div>

            <div className="force-password-change-innerContainer-header-title-info">
              В связи с изменениями политики конфиденциальности <br />
              необходимо сменить ваш пароль.
            </div>
          </Row>

          <Row className="force-password-change-innerContainer-middle">
            Пароль должен быть не менее 8 символов и содержать хотя бы по одному
            <br /> символу из четырёх следующих групп:
          </Row>

          <div className="force-password-change-innerContainer-content">{passwordChangeInfo()}</div>

          <Link to="/password-forgotten" className="text-bold">
            Забыли пароль?
          </Link>

          <div className="mt34 display-flex" style={{ justifyContent: 'center' }}>
            <Button
              style={{ width: 247, height: 40 }}
              onClick={() => passwordForm.submit()}
              disabled={isPassButtonDisabled}
            >
              Сохранить
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForcePasswordChange;
